import { Link } from 'react-router-dom';
import { DollarSign, Shield, Eye, BarChart3, TrendingUp, CheckCircle, Clock, Smartphone, Users, MessageCircle, Lock, FileText } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const ParkingRevenueManagementApp = () => {
    useDynamicSEO({
      title: "Parking Revenue Management App | Realtime Financial Tracking",
      description: "Gain revenue control with a Parking Revenue Management App that digitally records every transaction, prevents leakage & delivers realtime business insights.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Parking Revenue Management App | Realtime Financial Tracking",
      ogDescription: "Gain revenue control with a Parking Revenue Management App that digitally records every transaction, prevents leakage & delivers realtime business insights."
    });

  const handleWhatsAppClick = () => {
    const phoneNumber = '9384376329';
    const message = encodeURIComponent('Hi, I would like to request a demo of Park Hive Parking Revenue Management App for complete financial transparency and zero revenue leakage.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const revenueFeatures = [
    {
      icon: <Eye className="w-8 h-8 text-blue-600" />,
      title: "Real-time Revenue Visibility",
      description: "Live dashboard showing every rupee earned, collected, and processed with instant updates"
    },
    {
      icon: <Shield className="w-8 h-8 text-green-600" />,
      title: "Zero Leakage Protection",
      description: "Digital tracking eliminates cash handling risks and manual collection discrepancies"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-purple-600" />,
      title: "Automated Billing Rules",
      description: "Time-based pricing applied automatically with no manual calculations or errors"
    },
    {
      icon: <Lock className="w-8 h-8 text-red-600" />,
      title: "Operator Accountability",
      description: "Every action traceable by name, timestamp, and digital signature for complete transparency"
    },
    {
      icon: <FileText className="w-8 h-8 text-indigo-600" />,
      title: "Instant Financial Reports",
      description: "Exportable revenue reports generated with one tap for accounting and audit purposes"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-yellow-600" />,
      title: "Multi-location Control",
      description: "Monitor revenue across all parking locations from single dashboard view"
    }
  ];

  const revenuePains = [
    "Staff adjustments and excuses",
    "Disappearing cash collections", 
    "Wrong time or vehicle calculations",
    "No proof when disputes arise",
    "Lack of visibility into real earnings",
    "Difficulty scaling with confidence"
  ];

  const revenueProtections = [
    "Every check-in and check-out digitally recorded",
    "Time-based billing rules applied automatically",
    "UPI and digital payments eliminate cash leakage",
    "All operator actions traceable by name and timestamp",
    "Reports generated instantly and exportable with one tap",
    "Real-time dashboards update revenue flow live",
    "Multi-location parking revenue monitored in one view"
  ];

  const ownerBenefits = [
    "Absolute confidence in actual earnings",
    "Proof-backed control for auditing and settlement",
    "Peace of mind regardless of who operates the shift",
    "Data intelligence to improve pricing and profitability",
    "Freedom from daily monitoring pressure"
  ];

  const environmentTypes = [
    "Commercial Complexes",
    "Multiplex Chains", 
    "IT Parks",
    "Residential Communities",
    "Airports",
    "Hospitals",
    "Event Spaces",
    "Stadiums",
    "Tourist Parking Centers"
  ];

  const faqData = [
    {
      question: "Can I see revenue live from anywhere?",
      answer: "Yes — the dashboard updates in real time from any device."
    },
    {
      question: "Does it support both cash and digital payments?",
      answer: "Yes — with automated reconciliation."
    },
    {
      question: "Are reports exportable for accounts and audits?",
      answer: "Yes — instantly in downloadable format."
    },
    {
      question: "Can this prevent staff fraud or manipulation?",
      answer: "Yes — every action has a traceable digital trail."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 via-green-800 to-emerald-900 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <DollarSign className="w-16 h-16 text-green-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Parking Revenue Management App
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-green-200">
            100% Transparency, Real-Time Financial Monitoring & Zero Revenue Leakage
          </p>
          <p className="text-xl mb-10 max-w-4xl mx-auto">
            Smart digital revenue tracking designed to protect every rupee and eliminate financial blind spots in parking operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Request Live Demo
            </button>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-green-900 px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 text-center"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Core Problem */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The Daily Revenue Question Every Owner Asks
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
              <p className="text-2xl text-gray-800 italic mb-6">
                "How much revenue did my parking make today — and how much of it actually reached me?"
              </p>
              <p className="text-lg text-gray-600">
                In a world where cash handling is unpredictable, manual entries are unreliable, and staff dependency creates blind spots, 
                revenue becomes the biggest risk in parking operations.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Revenue Reality</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Money disappears between shifts</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Collections don't match reports</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Reconciliation becomes a guessing game</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Owners are left to trust instead of knowing</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-green-800 mb-4">The Digital Solution</h3>
              <p className="text-gray-700 mb-6">
                The future of profitable parking lies in digital revenue tracking — real-time, accurate, automated, and impossible to manipulate.
              </p>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-green-600">No Missing Money</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-green-600">No Mismatched Numbers</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-green-600">No Sleepless Nights</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-bold text-green-600">Pure Financial Clarity</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Revenue Needs Control */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Parking Revenue Needs Real Control
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Parking is one of the few businesses where money moves fast, manually, and often with little verification. 
              When operations rely on handwritten tickets and cash counters, revenue leakage becomes unavoidable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-red-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-red-800 mb-6">Constant Owner Pain Points</h3>
              <div className="space-y-4">
                {revenuePains.map((pain, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{pain}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-green-800 mb-6">App-Based Solution</h3>
              <p className="text-gray-700 mb-6">
                A Parking Revenue Management App ends this uncertainty forever. When every rupee is tracked digitally and automatically, 
                parking stops being a gamble and becomes a reliable business.
              </p>
              <div className="bg-green-100 p-6 rounded-lg">
                <p className="text-lg font-semibold text-green-800 text-center">
                  When every rupee is tracked digitally, parking becomes reliable business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Park Hive Protects Revenue */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Park Hive Protects Every Rupee
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              From entry to exit, every operational and financial action is recorded digitally with zero manual involvement.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Entry Scanned</h3>
              <p className="text-gray-600 text-sm">Digital vehicle identification starts revenue tracking</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Time Tracked</h3>
              <p className="text-gray-600 text-sm">Automatic duration monitoring without manual logs</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Billing Automated</h3>
              <p className="text-gray-600 text-sm">Pricing rules applied instantly and accurately</p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Payment Captured</h3>
              <p className="text-gray-600 text-sm">Digital transactions recorded transparently</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
              Complete Financial Intelligence
            </h3>
            <p className="text-lg text-gray-700 text-center max-w-4xl mx-auto">
              Owners receive real-time visibility of total revenue, peak hours, staff performance, vehicle logs, and occupancy-based projections. 
              The app turns financial blind spots into financial intelligence.
            </p>
            <div className="mt-8 text-center">
              <p className="text-xl font-bold text-green-600">No more chasing numbers. The truth is visible instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Transparency Features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              True Revenue Transparency
            </h2>
            <p className="text-xl text-gray-700">
              Complete digital tracking with real-time visibility and exportable proof
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {revenueFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-blue-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              With Park Hive Revenue Protection:
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {revenueProtections.map((protection, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">{protection}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-xl font-bold text-blue-600">
                You never need to depend on anyone's explanation. The numbers speak for themselves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Impact for Owners */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              The Real Impact for Parking Lot Owners
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Revenue becomes predictable</h3>
                <p className="text-green-100">instead of mysterious</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Collections become verifiable</h3>
                <p className="text-green-100">instead of debatable</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Financial management becomes simple</h3>
                <p className="text-green-100">instead of stressful</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-8">Owners Gain Complete Control:</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {ownerBenefits.map((benefit, index) => (
                <div key={index} className="bg-white bg-opacity-20 p-4 rounded-lg">
                  <CheckCircle className="w-8 h-8 mx-auto mb-3" />
                  <p className="text-sm font-medium">{benefit}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-white bg-opacity-20 p-6 rounded-xl">
              <p className="text-2xl font-bold">
                You finally manage the business — instead of managing suspicion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect for Every Environment */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Perfect for Every Parking Environment
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto mb-8">
              Whether your lot handles 100 vehicles or 10,000 — whether it runs daily or seasonal — 
              whether you operate one location or fifteen — revenue protection is guaranteed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {environmentTypes.map((type, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center">
                <Users className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900">{type}</h3>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white p-8 rounded-xl shadow-lg text-center">
            <p className="text-2xl text-gray-800 mb-4">
              All these environments depend on one thing above everything else:
            </p>
            <p className="text-3xl font-bold text-green-600 mb-6">Financial Trust</p>
            <p className="text-xl text-gray-700">Park Hive protects that trust.</p>
          </div>
        </div>
      </section>

      {/* Why Leaders Choose */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">
            Why Leaders Choose Park Hive
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Revenue is the heart</h3>
              <p className="text-gray-700 text-sm">of parking business</p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Transparency builds</h3>
              <p className="text-gray-700 text-sm">growth</p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Scalability demands</h3>
              <p className="text-gray-700 text-sm">automation</p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Guessing is not</h3>
              <p className="text-gray-700 text-sm">a strategy</p>
            </div>
          </div>

          <div className="bg-gray-900 text-white p-8 rounded-xl">
            <h3 className="text-2xl font-bold mb-6">Complete Business Intelligence</h3>
            <p className="text-lg mb-6">
              Park Hive gives owners complete visibility without being physically present and without relying on manual accounting.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="font-bold text-green-400">Profit becomes measurable</p>
              </div>
              <div>
                <p className="font-bold text-blue-400">Performance becomes accountable</p>
              </div>
              <div>
                <p className="font-bold text-purple-400">Decisions become data-backed</p>
              </div>
            </div>
            <p className="text-xl font-bold mt-8 text-yellow-400">
              This is professionalism in parking.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            See Revenue Transparency in Action
          </h2>
          <p className="text-xl mb-8">
            Watch real-time numbers update. Experience true financial control. See exactly how revenue transparency feels.
          </p>
          <button 
            onClick={handleWhatsAppClick}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <MessageCircle className="w-5 h-5" />
            Request a Demo - Own Your Revenue. Own Your Business.
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Revenue is the backbone of parking.
          </h2>
          <div className="space-y-4 text-lg mb-8">
            <p>If you can't measure it, you can't protect it.</p>
            <p>If you can't track it, you can't grow it.</p>
            <p>The smartest parking businesses don't rely on trust —</p>
            <p className="font-bold text-green-400">they rely on technology.</p>
          </div>
          <div className="bg-green-600 p-6 rounded-xl">
            <p className="text-2xl font-bold">
              Park Hive ensures revenue remains exactly where it belongs — with you.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParkingRevenueManagementApp;